import dayjs from 'dayjs';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useJsonToCsv } from 'react-json-csv';
import _ from 'underscore';

import { ScenarioResult, Summary, SummaryType } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';

import { Box, useTheme, Button, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { ReactComponent as BestBiologyIcon } from '../../../assets/best-biology-plan-icon.svg';
import { ReactComponent as BestEconomyIcon } from '../../../assets/best-economy-plan-icon.svg';
import { ReactComponent as BestEnvironmentIcon } from '../../../assets/best-environment-plan-icon.svg';
import TopPerformance from './../results/top-performance';
import getDecimalPlace from '../../../utils/get-decimal-place';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-result-context';

type Props = {
  renderTabOptions: () => JSX.Element;
  showTopPerformers: boolean;
  summaryResult: ScenarioResult | undefined;
  selectedSummaryType: SummaryType;
};

const TopPerformers: React.FC<Props> = ({ renderTabOptions, showTopPerformers, selectedSummaryType }) => {
  const theme = useTheme();
  const { saveAsCsv } = useJsonToCsv();
  const intl = useIntl();
  const PAGE_NAME_ID = 'bluViuNutrition.viewScenarioResultGt2';
  const csvExportResultsDialog = useRef<React.ElementRef<typeof Dialog>>(null);

  const {
    scenarioQuery: { data: scenario },
  } = useScenarioState();

  const { isLoading, summaries, feedPlans, displayedColumns, showGraph } = useScenarioResultState();

  const [csvfeedProgamsFilter, setCsvFeedProgramsFilter] = useState<string>('');

  const { lowestCo2Plan, bestBiologyPlan, mostEconomicalPlan } = feedPlans;
  const weeks = Number(feedPlans.bestBiologyPlan.daysToReach) / 7;

  const formatIconNumber = (number: number | string | undefined) => {
    if (number === undefined) return 0;

    const checkInteger = Number(number) % 1 === 0;
    return checkInteger ? intl.formatMessage({ id: `${PAGE_NAME_ID}.fixedNumber` }) : Number(number);
  };

  const csvfeedProgramsComparisionFields: (keyof Summary)[] = [
    'finalWeightGrams',
    'daysToReach',
    'weightGainGrams',
    'feedIntakeGrams',
    'bFcr',
    'carcassYieldPercent',
    'filletFatPercent',
    'hogGainGrams',
    'globalWarmingPotentialCo2KgPerKgGrowth',
    'averageFeedCost',
    'feedCostOfGrowth',
    'feedCostOfHogGrowth',
  ];

  const getUnit = (propertyName: keyof Summary): string | undefined => {
    if (['finalWeightGrams', 'weightGainGrams', 'feedIntakeGrams', 'hogGainGrams'].includes(propertyName)) return 'g';
    return propertyName === 'globalWarmingPotentialCo2KgPerKgGrowth' ? 'kg' : undefined;
  };

  const getReadableFieldLabel = (field: keyof Summary): string => {
    return field
      .replace(/Grams|Percent|KgPerKgGrowth|Co2/g, '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatNumber = (value: number, decimals: number): string => {
    return value.toLocaleString(undefined, {
      maximumFractionDigits: decimals,
    });
  };

  const formatDiff = (value: number, decimals: number, unit?: string, isPercent = false): string => {
    if (isNaN(value)) return '0';
    const rounded = formatNumber(value, decimals);
    const prefix = value > 0 ? '+' : value < 0 ? '' : '';
    const suffix = isPercent ? '%' : (unit ?? '');
    return `${prefix}${rounded}${suffix ? ' ' + suffix : ''}`.trim();
  };

  const prepareFeedComparison = (
    summaries: Summary[],
    referenceFeedPlan: string,
    fieldsToCompare: (keyof Summary)[]
  ): Record<string, string>[] | null => {
    const reference = summaries.find((s) => s.feedPlan === referenceFeedPlan);
    if (!reference) return null;

    const output: Record<string, string>[] = [];

    for (const field of fieldsToCompare) {
      const refValue = Number(reference[field]);
      const unit = getUnit(field);
      const decimalPlaces = getDecimalPlace(field);

      const row: Record<string, string> = {
        Field: getReadableFieldLabel(field),
      };

      for (const summary of summaries) {
        const currValue = Number(summary[field]);
        const feedPlan = summary.feedPlan;

        let value: string;
        if (feedPlan === referenceFeedPlan) {
          value = `${formatNumber(currValue, decimalPlaces)} ${unit || ''} (0)`.trim();
        } else {
          const absDiff = currValue - refValue;
          const relDiff = refValue !== 0 ? (absDiff / refValue) * 100 : 0;

          const absFormatted = formatDiff(absDiff, decimalPlaces, unit);
          const relFormatted = formatDiff(relDiff, 1, undefined, true);

          value = `${absFormatted} (${relFormatted})`;
        }

        row[feedPlan] = `"${value}"`;
      }

      output.push(row);
    }

    return output;
  };

  const feedsProgramsCsvData = scenario?.feedPrograms.flatMap((program) =>
    (program.feeds || []).map((feed) => ({
      'Program Name': program.name,
      Feed: feed.feedName,
      'Fish size': feed.fishSizeStart,
      'Customer Price': feed.price,
    }))
  );

  const exportCsvResults = (feedplan: string) => {
    if (!summaries || !feedplan) return;
    const feedsCompareresult = prepareFeedComparison(summaries, feedplan, csvfeedProgramsComparisionFields);

    // feed programs CSV
    saveAsCsv({
      data: feedsProgramsCsvData,
      filename: `feed_programs-${dayjs().format('L LT')}`,
      fields: {
        'Program Name': 'Program Name',
        Feed: 'Feed',
        'Fish size': 'Fish size',
        'Customer Price': 'Customer Price',
      },
    });

    // feed programs comaprision CSV
    saveAsCsv({
      data: feedsCompareresult,
      filename: `comparison-${dayjs().format('L LT')}`,
      fields: {
        Field: 'Parameter',
        ...summaries?.reduce(
          (previous, current) => ({
            ...previous,
            [current.feedPlan]:
              current.feedPlan.charAt(0).toUpperCase() + current.feedPlan.slice(1).replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
          }),
          {}
        ),
      },
    });

    // scenario result summary CSV
    saveAsCsv({
      data: summaries,
      filename: `scenario_results-${dayjs().format('L LT')}`,
      fields: displayedColumns.reduce(
        (previous, current) =>
          Object.assign(previous, {
            [current]: current.charAt(0).toUpperCase() + current.slice(1).replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
          }),
        {}
      ),
    });
  };

  return (
    <Box display={'flex'} flexDirection={'column'} paddingBottom={10}>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} marginBottom={10}>
          {renderTabOptions()}
          <Dialog
            maxWidth="sm"
            ref={csvExportResultsDialog}
            onClose={() => {
              setCsvFeedProgramsFilter('');
            }}
            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.referenceFeedProgram` })}
            button={
              <Button size="small">
                <FormattedMessage id={`${PAGE_NAME_ID}.exportCSVBtn`} />
              </Button>
            }
          >
            <Box>
              <Box display="flex" flexDirection="column" gap={6}>
                <Select
                  dark
                  label="Feed Programs"
                  value={csvfeedProgamsFilter}
                  onChange={(event) => {
                    setCsvFeedProgramsFilter(event.target.value as string);
                  }}
                >
                  {summaries?.map((fp, idx) => (
                    <RadioSelectItem key={`feed-${idx}`} value={fp.feedPlan}>
                      {fp.feedPlan}
                    </RadioSelectItem>
                  ))}
                </Select>
                <Button
                  disabled={!csvfeedProgamsFilter}
                  size="small"
                  sx={{ width: '10%' }}
                  onClick={() => {
                    csvExportResultsDialog?.current?.setOpen(false);
                    setCsvFeedProgramsFilter('');
                    exportCsvResults(csvfeedProgamsFilter);
                  }}
                >
                  <FormattedMessage id={'general.ok'} />
                </Button>
              </Box>
            </Box>
          </Dialog>
        </Box>
      )}
      {!showGraph && showTopPerformers && summaries && summaries?.length > 1 && (
        <Box>
          <Typography variant="h5">
            <FormattedMessage id={`${PAGE_NAME_ID}.topPerformers`} />
          </Typography>
          <TopPerformance
            items={[
              {
                title: `${PAGE_NAME_ID}.icons.lowestCo2Title`,
                planName: lowestCo2Plan.name,
                text: intl.formatMessage(
                  { id: `${PAGE_NAME_ID}.lowestCo2Feed` },
                  { percentage: formatIconNumber(lowestCo2Plan.percentage?.toFixed(2)) }
                ),
                icon: <BestEnvironmentIcon />,
              },
              {
                title: `${PAGE_NAME_ID}.icons.bestBioTitle`,
                planName: bestBiologyPlan.name,
                text: intl.formatMessage(
                  {
                    id:
                      selectedSummaryType === SummaryType.Week
                        ? `${PAGE_NAME_ID}.bestBiologyFeedWeek`
                        : `${PAGE_NAME_ID}.bestBiologyFeedWeight`,
                    defaultMessage: '0',
                  },
                  selectedSummaryType === SummaryType.Week
                    ? {
                        percentage: formatIconNumber(bestBiologyPlan.percentage?.toFixed(2)),
                        week: weeks,
                      }
                    : { daysToReach: bestBiologyPlan?.daysToReach }
                ),
                icon: <BestBiologyIcon />,
              },
              {
                title: `${PAGE_NAME_ID}.icons.mostEconomicalTitle`,
                planName: mostEconomicalPlan.name,
                text: intl.formatMessage(
                  { id: `${PAGE_NAME_ID}.mostEconomicalFeed` },
                  { percentage: formatIconNumber(mostEconomicalPlan.percentage?.toFixed(2)) }
                ),
                icon: <BestEconomyIcon />,
              },
            ]}
          />
        </Box>
      )}
    </Box>
  );
};

export default TopPerformers;
